/** @jsxImportSource @emotion/react */
import React from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import Button from '../../../../components/ts-components/Button'
import { DateOfBirthInput } from '../../../../components/ts-components/DateOfBirthInput'
import Text from '../../../../components/ts-components/Text'
import { TextInput } from '../../../../components/ts-components/TextInput'
import type {
  InviteSignupFieldErrors,
  InviteSignupFormErrors,
  InviteSignupLoading,
} from '../types'

type InviteSignupFormProps = {
  nameFirst: string
  nameLast: string
  password: string
  confirmPassword: string
  birthDate: string
  setNameFirst: (value: string) => void
  setNameLast: (value: string) => void
  setPassword: (value: string) => void
  setConfirmPassword: (value: string) => void
  setBirthDate: (value: string) => void
  errors: InviteSignupFormErrors
  fieldErrors: InviteSignupFieldErrors
  loading: InviteSignupLoading
  passwordsMatch: boolean
  isPlayerInvite: boolean
  onSubmit: (event?: React.FormEvent) => void
}

/**
 * The signup fields.
 *
 * Errors arrive already mapped from the API; this component only renders them.
 * The submit button is disabled while registering and at no other time — the
 * remaining gates live in `onSubmit`, as they always have.
 */
const InviteSignupForm = ({
  nameFirst,
  nameLast,
  password,
  confirmPassword,
  birthDate,
  setNameFirst,
  setNameLast,
  setPassword,
  setConfirmPassword,
  setBirthDate,
  errors,
  fieldErrors,
  loading,
  passwordsMatch,
  isPlayerInvite,
  onSubmit,
}: InviteSignupFormProps) => {
  const isRegistering = loading.register

  /*
   * The shared inputs look their message up by `name` in a `{ field: message }`
   * map, so build one map keyed by the field names this form uses.
   *
   * Both password fields also carry `errors.password` — the client-side
   * "passwords do not match" message — on top of their own API error, which is
   * how the legacy form surfaced it on both at once.
   */
  const inputErrors: Record<string, string> = {}
  if (fieldErrors.firstNameError) {
    inputErrors.nameFirst = fieldErrors.firstNameError
  }
  if (fieldErrors.lastNameError) {
    inputErrors.nameLast = fieldErrors.lastNameError
  }
  if (fieldErrors.dateOfBirthError) {
    inputErrors.birthDate = fieldErrors.dateOfBirthError
  }
  const passwordMessage = errors.password || fieldErrors.passwordError
  if (passwordMessage) {
    inputErrors.password = passwordMessage
  }
  const confirmMessage = errors.password || fieldErrors.confirmPasswordError
  /*
   * Canlan's translation already ends in " *". `required` draws the marker, so
   * a star in the source string shows up as a second one.
   */
  const confirmPasswordLabel = t('AccountScreen:confirmPassword', {}).replace(
    /\s*\*+\s*$/,
    ''
  )
  if (confirmMessage) {
    inputErrors.confirmPassword = confirmMessage
  }

  // Green check on confirm only once both are filled and matching.
  const showMatch = Boolean(password && confirmPassword && passwordsMatch)

  return (
    <form css={styles.form} onSubmit={onSubmit} noValidate>
      {errors.form ? (
        <div css={styles.banner} role='alert'>
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.ERROR_LIGHT}
          >
            {errors.form}
          </Text>
        </div>
      ) : null}

      <div css={styles.row}>
        <div css={styles.cell}>
          <TextInput
            name='nameFirst'
            value={nameFirst}
            setValue={setNameFirst}
            fieldErrors={inputErrors}
            customError={null}
            label={t('AccountScreen:firstName', {})}
            placeholder={capitalizeWords(t('AccountScreen:firstName', {}))}
            autoComplete='given-name'
            disabled={isRegistering}
            error={Boolean(inputErrors.nameFirst)}
            required
          />
        </div>
        <div css={styles.cell}>
          <TextInput
            name='nameLast'
            value={nameLast}
            setValue={setNameLast}
            fieldErrors={inputErrors}
            customError={null}
            label={t('AccountScreen:lastName', {})}
            placeholder={capitalizeWords(t('AccountScreen:lastName', {}))}
            autoComplete='family-name'
            disabled={isRegistering}
            error={Boolean(inputErrors.nameLast)}
            required
          />
        </div>
      </div>

      {isPlayerInvite ? (
        <DateOfBirthInput
          name='birthDate'
          value={birthDate}
          setValue={setBirthDate}
          fieldErrors={inputErrors}
          customError={null}
          label={capitalizeWords(t('Auth:dateOfBirth', {}))}
          disabled={isRegistering}
        />
      ) : null}

      <div css={styles.row}>
        <div css={styles.cell}>
          <TextInput
            name='password'
            value={password}
            setValue={setPassword}
            fieldErrors={inputErrors}
            customError={null}
            label={t('common:password', {})}
            placeholder={capitalizeWords(t('common:password', {}))}
            type='password'
            autoComplete='new-password'
            disabled={isRegistering}
            error={Boolean(inputErrors.password)}
            required
          />
        </div>
        <div css={styles.cell}>
          <TextInput
            name='confirmPassword'
            value={confirmPassword}
            setValue={setConfirmPassword}
            fieldErrors={inputErrors}
            customError={null}
            label={confirmPasswordLabel}
            placeholder={capitalizeWords(confirmPasswordLabel)}
            type='password'
            autoComplete='new-password'
            disabled={isRegistering}
            success={showMatch}
            error={Boolean(inputErrors.confirmPassword)}
            required
          />
        </div>
      </div>

      {/*
        `Button` renders a type='button', so it cannot submit the form itself —
        it calls the handler directly. The surrounding <form onSubmit> is what
        makes Enter-to-submit work from inside a field.
      */}
      <Button
        text={t('Auth:createAccount', {})}
        variant='primary'
        onClick={() => onSubmit()}
        isLoading={isRegistering}
        disabled={isRegistering}
        style={styles.submit}
      />

      <Text
        variant='paragraphXSmall'
        weight='regular'
        color={colors.NEUTRAL_50}
        style={'text-align: center;'}
      >
        {t('Auth:byCreatingAnAccount', {})}
        <a
          css={styles.link}
          href='/docs/eula.pdf'
          target='_blank'
          rel='noreferrer noopener'
        >
          {t('Auth:termsOfUse', {})}
        </a>
      </Text>
    </form>
  )
}

export default InviteSignupForm

const styles = {
  form: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  `,
  row: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  `,
  /* Each cell owns one field so the label/control/error stack stays together. */
  cell: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  `,
  banner: css`
    border-radius: 8px;
    border: 1px solid ${colors.ERROR_300};
    background: rgba(217, 27, 27, 0.12);
    padding: 8px 12px;
  `,
  submit: css`
    width: 100%;
    flex: none;
  `,
  link: css`
    color: ${colors.NEUTRAL_0};
    text-decoration: underline;
    text-underline-offset: 2px;
  `,
}
